import React from 'react'
import { Bar } from 'react-chartjs-2';
import { useTranslation } from 'react-i18next';
import { generateDataLabelPluginConfig } from '../../utils/Config/DataLabelPluginConfig';
import { calculateYAxisScale } from './yAxisScale';

const BarChartProjectReport = ({ chart }) => {
    const { t } = useTranslation();
    const currentYear = new Date().getFullYear();
    const currentProjectionData = chart.map((item) =>
        item.year === currentYear ? item.currentValue ?? null : null
    );
    const hasCurrentProjection = currentProjectionData.some((value) => value !== null);

    const disbursedDataSource = chart.map((item) => item.disbursed);

    const data = {
        labels: chart.map((item) => item.year),
        datasets: [
            {
                label: t('amount_disbursed'),
                type: "bar",
                data: disbursedDataSource,
                backgroundColor: "#538cff",
                borderColor: "#538cff",
                borderWidth: 2,
                order: 2,
                barThickness: 15,
                barPercentage: 0.1,
                categoryPercentage: 0.5,
                datalabels: {
                    display: false
                }
            },
            {
                label: t('original_projection'),
                type: "line",
                data: chart.map((item) => item.projected),
                borderColor: "grey",
                pointBackgroundColor: "white",
                borderWidth: 3,
                fill: false,
                pointRadius: 6,
                lineTension: 0,
                barPercentage: 0.1,
                categoryPercentage: 0.5,
                order: 1,
                datalabels: {
                    anchor: 'center',
                    offset: 10,
                    backgroundColor: '#FFFFFF',
                    borderRadius: 4,
                    textShadowColor: 'transparent',
                    padding: { top: 3, bottom: 3, left: 6, right: 6 },
                    align: ({ dataIndex, dataset }) => {
                        return dataIndex === dataset.data.length - 1 ? 'left' : 'right';
                    },
                },
            },
            ...(hasCurrentProjection ? [{
                label: t('current_projection'),
                type: "bar",
                data: currentProjectionData,
                backgroundColor: "#7A8AA9",
                borderColor: "#7A8AA9",
                borderWidth: 2,
                order: 2,
                barThickness: 15,
                barPercentage: 0.1,
                categoryPercentage: 0.5,
                datalabels: {
                    display: (context) => {
                        const value = context.dataset.data[context.dataIndex];
                        return typeof value === 'number' && Number.isFinite(value);
                    },
                    align: 'end',
                    anchor: 'end',
                    font: { weight: 'bold' },
                },
            }] : []),
        ],
    };

    const yAxisScale = calculateYAxisScale(data.datasets.flatMap((dataset) => dataset.data));
    const yAxisTitle = [t('millions_word'), t('dolar_sign')];
    const yAxisTitleFont = '12px Arial';

    const options = {
        responsive: true,
        maintainAspectRatio: false,
        devicePixelRatio: 3,
        layout: {
            padding: { bottom: 10, top: 42 }
        },
        plugins: {
            datalabels: {
                display: true,
                align: 'right',
                anchor: 'start',
                formatter: (value: number) => `${(value / 1000000).toFixed(1)} MM`,
                color: '#5A6A8A',
                font: {
                    size: 12,
                    weight: 300,
                },
            },
        },
        scales: {
            xAxes: [
                {
                    gridLines: { display: false },
                    ticks: { fontSize: 15 }
                },
            ],
            yAxes: [
                {
                    scaleLabel: { display: false },
                    afterFit: (scale) => {
                        const ctx = scale.ctx;
                        ctx.save();
                        ctx.font = yAxisTitleFont;
                        const titleWidth = Math.max(...yAxisTitle.map((line) => ctx.measureText(line).width));
                        scale.width = Math.max(scale.width, titleWidth + 15);
                        ctx.restore();
                    },
                    gridLines: { display: true },
                    ticks: {
                        fontSize: 15,
                        beginAtZero: true,
                        padding: 5,
                        autoSkip: false,
                        ...yAxisScale,
                        callback: (value: number) => value / 1000000,
                    },
                },
            ],
        },
        legend: {
            display: false
        }
    };

    const customLabelsPlugin = generateDataLabelPluginConfig(disbursedDataSource);
    const yAxisPlugin = {
        id: 'projectReportYAxis',
        beforeLayout: (chartInstance) => {
            const visibleValues = chartInstance.data.datasets
                .filter((_dataset, index) => chartInstance.isDatasetVisible(index))
                .flatMap((dataset) => dataset.data);
            Object.assign(chartInstance.options.scales.yAxes[0].ticks, calculateYAxisScale(visibleValues));
        },
        afterDraw: (chartInstance) => {
            const scale = chartInstance.scales['y-axis-0'];
            if (!scale) return;

            const ctx = chartInstance.ctx;
            ctx.save();
            ctx.font = yAxisTitleFont;
            ctx.fillStyle = '#5A6A8A';
            ctx.textAlign = 'right';
            ctx.textBaseline = 'bottom';
            yAxisTitle.forEach((line, index) => {
                ctx.fillText(line, scale.right - 15, scale.top - 30 + index * 14);
            });
            ctx.restore();
        },
    };
    const originalProjectionLabelShadowPlugin = {
        id: 'originalProjectionLabelShadow',
        afterUpdate: (chartInstance) => {
            chartInstance.getDatasetMeta(1).data.forEach((point) => {
                (point.$datalabels || []).forEach((label) => {
                    const drawLabel = label.draw;
                    label.draw = function (instance, center) {
                        const ctx = instance.ctx;
                        const pixelRatio = instance.currentDevicePixelRatio || 1;
                        ctx.save();
                        try {
                            ctx.shadowColor = '#26437726';
                            ctx.shadowBlur = 6 * pixelRatio;
                            ctx.shadowOffsetX = 0;
                            ctx.shadowOffsetY = 2 * pixelRatio;
                            drawLabel.call(this, instance, center);
                        } finally {
                            ctx.restore();
                        }
                    };
                });
            });
        },
    };
    const currentProjectionOffsetPlugin = {
        afterDatasetUpdate: (_chartInstance, { index, meta }) => {
            if (!hasCurrentProjection || index !== 2) return;

            // Offset the bar model so labels and hit detection follow its position.
            meta.data.forEach((bar) => {
                bar._model.x += 30;
            });
        },
    };

    return (
        <div style={{
            display: "flex",
            flexDirection: "column",
            flexGrow: 1,
            minHeight: 0,
            minWidth: 0,
            width: "100%",
            height: "100%",
            overflow: "hidden"
        }}>
            <Bar data={data} options={options} plugins={[customLabelsPlugin, currentProjectionOffsetPlugin, originalProjectionLabelShadowPlugin, yAxisPlugin]} />
        </div>
    );
};

export default BarChartProjectReport;

